import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { sessionAPI, applicationAPI, otpAPI, uploadAPI } from '../../services/api';
// Removed country identification imports - using simple fields now
import toast from 'react-hot-toast';
import OTPVerification from '../../components/OTPVerification';

const PublicApplicationForm = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    
    // OTP verification state
    const [email, setEmail] = useState('');
    const [otpSent, setOtpSent] = useState(false);
    const [otpVerified, setOtpVerified] = useState(false);
    const [sendingOtp, setSendingOtp] = useState(false);
    
    // Application state
    const [currentStage, setCurrentStage] = useState(1);
    const [sessionId, setSessionId] = useState(null);
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [sessionsLoading, setSessionsLoading] = useState(true);
    const [error, setError] = useState('');
    const [applicationId, setApplicationId] = useState(null);

    // Stage data
    const [stage1, setStage1] = useState({
        first_name: '',
        last_name: '',
        email: '',
        contact_number: ''
    });

    const [stage2, setStage2] = useState({
        highest_qualification: '',
        institution_name: '',
        year_of_passing: '',
        address_country: 'Nigeria',
        address_state: '',
        address_city: '',
        address_village: '',
        date_of_birth: '',
        gender: '',
        marital_status: '',
        number_of_kids: '',
        current_occupation: ''
    });

    const [stage3, setStage3] = useState({
        government_id_number: '',
        passport_number: '',
        government_id_path: '',
        educational_certificates_path: '',
        passport_photo_path: ''
    });

    const [stage4, setStage4] = useState({
        motivation_text: '',
        career_goals: '',
        financial_background: '',
        code_of_conduct_agreed: false,
        show_code_of_conduct: false,
        willing_to_relocate: false,
        has_equipment: false
    });

    // Load active sessions
    useEffect(() => {
        let isActive = true;

        const loadSessions = async () => {
            try {
                setSessionsLoading(true);
                const response = await sessionAPI.getActive();
                const sessionsList = response.sessions || [];

                if (!isActive) return;

                setSessions(sessionsList);

                const sessionParam = searchParams.get('session');
                let targetSessionId = null;

                if (sessionParam) {
                    const sessionExists = sessionsList.some(s => s.id === parseInt(sessionParam));
                    if (sessionExists) {
                        targetSessionId = parseInt(sessionParam);
                    }
                }

                if (!targetSessionId && sessionsList.length > 0) {
                    targetSessionId = sessionsList[0].id;
                }

                if (targetSessionId) {
                    setSessionId(targetSessionId);
                }

                setSessionsLoading(false);
            } catch (err) {
                console.error('Failed to load sessions:', err);
                if (isActive) {
                    setSessionsLoading(false);
                    setError('Failed to load sessions. Please try again later.');
                    setSessions([]);
                }
            }
        };

        loadSessions();

        return () => {
            isActive = false;
        };
    }, [searchParams]);

    // Removed country-based identification logic - now using simple fields in stage3

    // Pure validation function (no side effects)
    const isStageValid = (stage) => {
        switch(stage) {
            case 1:
                return !!(stage1.first_name && stage1.last_name && stage1.email);
            case 2:
                return !!(stage2.highest_qualification && stage2.institution_name && stage2.year_of_passing && 
                    stage2.address_country && stage2.address_state && stage2.address_city && stage2.address_village &&
                    stage2.date_of_birth && stage2.marital_status && stage2.current_occupation);
            case 3:
                // Only passport photo is mandatory; government ID and educational certificates are optional
                if (!stage3.passport_photo_path) {
                    return false;
                }
                // Validate government ID number is provided
                if (!stage3.government_id_number) {
                    return false;
                }
                return true;
            case 4:
                // Check all required fields are filled
                if (!stage4.motivation_text || !stage4.career_goals || !stage4.code_of_conduct_agreed) {
                    return false;
                }
                // Check both questions are answered
                if (stage4.willing_to_relocate === undefined || stage4.has_equipment === undefined) {
                    return false;
                }
                // Cannot submit if both are NO
                if (stage4.willing_to_relocate === false && stage4.has_equipment === false) {
                    return false;
                }
                return true;
            default:
                return true;
        }
    };

    // Validation function with error messages
    const validateStage = (stage) => {
        switch(stage) {
            case 1:
                if (!stage1.first_name || !stage1.last_name || !stage1.email) {
                    setError('Please fill in all required fields in Stage 1');
                    return false;
                }
                return true;
            case 2:
                if (!stage2.highest_qualification || !stage2.institution_name || !stage2.year_of_passing || 
                    !stage2.address_country || !stage2.address_state || !stage2.address_city || !stage2.address_village ||
                    !stage2.date_of_birth || !stage2.marital_status || !stage2.current_occupation) {
                    setError('Please fill in all required fields in Stage 2');
                    return false;
                }
                return true;
            case 3:
                if (!stage3.government_id_number) {
                    setError('Please provide your Government ID number (National ID or Voter ID)');
                    return false;
                }
                // Only passport photo is mandatory; government ID and educational certificates are optional
                if (!stage3.passport_photo_path) {
                    setError('Please upload your passport photo in Stage 3');
                    return false;
                }
                return true;
            case 4:
                if (!stage4.motivation_text || !stage4.career_goals || !stage4.code_of_conduct_agreed) {
                    setError('Please complete all required fields in Stage 4');
                    return false;
                }
                if (stage4.willing_to_relocate === undefined || stage4.has_equipment === undefined) {
                    setError('Please answer all questions in Stage 4');
                    return false;
                }
                // Validation: Cannot submit if both are NO (not willing to relocate AND no equipment)
                if (stage4.willing_to_relocate === false && stage4.has_equipment === false) {
                    setError('You must either be willing to relocate to Ojoto OR have personal laptop/desktop with steady power supply and internet access to participate in this program.');
                    return false;
                }
                return true;
            default:
                return true;
        }
    };

    // Handle OTP sending
    const handleSendOTP = async (e) => {
        e.preventDefault();
        if (!email || !email.includes('@')) {
            toast.error('Please enter a valid email address');
            return;
        }

        // DEVELOPMENT MODE: Auto-verify with hardcoded OTP
        const isDevelopment = import.meta.env.DEV || window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        
        if (isDevelopment) {
            console.log('🔧 DEVELOPMENT MODE: Auto-verifying OTP');
            setSendingOtp(true);
            setError('');
            try {
                // Send OTP (still creates user record)
                await otpAPI.send(email);
                // Immediately verify with hardcoded OTP
                await otpAPI.verify(email, '123456');
                setOtpSent(true);
                setOtpVerified(true);
                setStage1({ ...stage1, email });
                toast.success('Email verified (DEV MODE - OTP bypassed)');
            } catch (err) {
                setError(err.message || 'Failed to verify OTP');
                toast.error(err.message || 'Failed to verify OTP');
            } finally {
                setSendingOtp(false);
            }
            return;
        }

        setSendingOtp(true);
        setError('');
        try {
            await otpAPI.send(email);
            setOtpSent(true);
            toast.success('OTP sent to your email');
        } catch (err) {
            setError(err.message || 'Failed to send OTP');
            toast.error(err.message || 'Failed to send OTP');
        } finally {
            setSendingOtp(false);
        }
    };

    // Handle OTP verification
    const handleOTPVerified = () => {
        setOtpVerified(true);
        setStage1({ ...stage1, email });
        toast.success('Email verified! You can now proceed with your application.');
    };

    // Handle OTP resend
    const handleOTPResend = () => {
        toast.success('OTP resent to your email');
    };

    // Save application draft
    const saveApplication = async () => {
        if (!otpVerified || !email) {
            toast.error('Please verify your email first');
            return;
        }

        setLoading(true);
        try {
            // Filter out UI-only fields from stage4 before sending to backend
            const { show_code_of_conduct, ...stage4Data } = stage4;
            
            // Extract identification numbers from stage3 (they go into identification_data, not as separate fields)
            const { government_id_number, passport_number, ...stage3Data } = stage3;
            
            const data = {
                email,
                session_id: sessionId,
                stage: currentStage,
                data: {
                    ...stage1,
                    ...stage2,
                    ...stage3Data, // stage3 without government_id_number and passport_number
                    ...stage4Data, // Use filtered stage4 data (without show_code_of_conduct)
                    identification_data: JSON.stringify({
                        government_id_number: government_id_number || '',
                        passport_number: passport_number || ''
                    })
                }
            };
            const response = await applicationAPI.createOrUpdatePublic(data);
            if (response.applicationId) {
                setApplicationId(response.applicationId);
            }
            return response.applicationId || applicationId; // Return the application ID
        } catch (err) {
            setError(err.message || 'Failed to save application');
            toast.error(err.message || 'Failed to save application');
            throw err;
        } finally {
            setLoading(false);
        }
    };

    // Submit final application
    const submitApplication = async () => {
        // Validate all stages
        if (!validateStage(1) || !validateStage(2) || !validateStage(3) || !validateStage(4)) {
            setError('Please complete all stages before submitting');
            return;
        }

        setLoading(true);
        setError('');

        try {
            // Save all data first and get application ID
            const savedApplicationId = await saveApplication();
            const finalApplicationId = savedApplicationId || applicationId;

            if (!finalApplicationId) {
                setError('No application found to submit. Please try again.');
                return;
            }

            const response = await applicationAPI.submitPublic(email, finalApplicationId);
            
            if (response.success) {
                toast.success('Application submitted successfully!');
                
                // Auto-login if token provided
                if (response.token) {
                    localStorage.setItem('token', response.token);
                    localStorage.setItem('user', JSON.stringify(response.user));
                }

                // Password will be sent via email when applicant is selected
                // No need to display it here

                setTimeout(() => {
                    navigate(`/application-success?applicationId=${finalApplicationId}`);
                }, 2000);
            }
        } catch (err) {
            setError(err.message || 'Failed to submit application');
            toast.error(err.message || 'Failed to submit application');
        } finally {
            setLoading(false);
        }
    };

    // Handle stage navigation
    const handleStageSubmit = async () => {
        if (!validateStage(currentStage)) {
            return;
        }

        if (currentStage < 4) {
            try {
                await saveApplication();
                setError('');
                setCurrentStage(currentStage + 1);
                toast.success(`Stage ${currentStage} saved successfully!`);
            } catch (err) {
                // Error already set in saveApplication
            }
        } else {
            await submitApplication();
        }
    };

    // Render functions for each stage
    const renderStage1 = () => (
        <div className="space-y-4">
            <h3 className="text-xl font-bold">Stage 1: Basic Details</h3>
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium mb-1">First Name *</label>
                    <input
                        type="text"
                        value={stage1.first_name}
                        onChange={(e) => setStage1({...stage1, first_name: e.target.value})}
                        className="w-full px-3 py-2 border rounded"
                        required
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium mb-1">Last Name *</label>
                    <input
                        type="text"
                        value={stage1.last_name}
                        onChange={(e) => setStage1({...stage1, last_name: e.target.value})}
                        className="w-full px-3 py-2 border rounded"
                        required
                    />
                </div>
            </div>
            <div>
                <label className="block text-sm font-medium mb-1">Email *</label>
                <input
                    type="email"
                    value={stage1.email || email}
                    onChange={(e) => setStage1({...stage1, email: e.target.value})}
                    className="w-full px-3 py-2 border rounded bg-gray-100"
                    required
                    disabled
                />
                <p className="text-xs text-gray-500 mt-1">Email is locked after verification</p>
            </div>
            <div>
                <label className="block text-sm font-medium mb-1">Contact Number *</label>
                <input
                    type="tel"
                    value={stage1.contact_number}
                    onChange={(e) => setStage1({...stage1, contact_number: e.target.value})}
                    className="w-full px-3 py-2 border rounded"
                    placeholder="+1234567890"
                    required
                />
            </div>
        </div>
    );

    const renderStage2 = () => (
        <div className="space-y-4">
            <h3 className="text-xl font-bold">Stage 2: Educational & Personal Details</h3>
            <div>
                <label className="block text-sm font-medium mb-1">Highest Qualification *</label>
                <select
                    value={stage2.highest_qualification}
                    onChange={(e) => setStage2({...stage2, highest_qualification: e.target.value})}
                    className="w-full px-3 py-2 border rounded"
                    required
                >
                    <option value="">Select...</option>
                    <option value="ssce">SSCE</option>
                    <option value="nd">ND</option>
                    <option value="hnd">HND</option>
                    <option value="bachelor">Bachelor's Degree</option>
                    <option value="master">Master's Degree</option>
                    <option value="phd">PhD</option>
                </select>
            </div>
            <div>
                <label className="block text-sm font-medium mb-1">Institution Name *</label>
                <input
                    type="text"
                    value={stage2.institution_name}
                    onChange={(e) => setStage2({...stage2, institution_name: e.target.value})}
                    className="w-full px-3 py-2 border rounded"
                    required
                />
            </div>
            <div>
                <label className="block text-sm font-medium mb-1">Year of Passing *</label>
                <input
                    type="number"
                    value={stage2.year_of_passing}
                    onChange={(e) => setStage2({...stage2, year_of_passing: e.target.value})}
                    className="w-full px-3 py-2 border rounded"
                    min="1990"
                    max={new Date().getFullYear()}
                    required
                />
            </div>
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium mb-1">Country *</label>
                    <select
                        value={stage2.address_country}
                        onChange={(e) => setStage2({...stage2, address_country: e.target.value})}
                        className="w-full px-3 py-2 border rounded"
                        required
                    >
                        <option value="Nigeria">Nigeria</option>
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium mb-1">State *</label>
                    <input
                        type="text"
                        value={stage2.address_state}
                        onChange={(e) => setStage2({...stage2, address_state: e.target.value})}
                        className="w-full px-3 py-2 border rounded"
                        required
                    />
                </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium mb-1">City *</label>
                    <input
                        type="text"
                        value={stage2.address_city}
                        onChange={(e) => setStage2({...stage2, address_city: e.target.value})}
                        className="w-full px-3 py-2 border rounded"
                        required
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium mb-1">Village *</label>
                    <select
                        value={stage2.address_village}
                        onChange={(e) => setStage2({...stage2, address_village: e.target.value})}
                        className="w-full px-3 py-2 border rounded"
                        required
                    >
                        <option value="">Select Village...</option>
                        <option value="Ezieke">Ezieke</option>
                        <option value="Ojo">Ojo</option>
                        <option value="Enugo">Enugo</option>
                        <option value="Ire">Ire</option>
                        <option value="Ndiabor">Ndiabor</option>
                        <option value="Umuezema">Umuezema</option>
                        <option value="Umuchem">Umuchem</option>
                        <option value="Ezema">Ezema</option>
                    </select>
                </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium mb-1">Date of Birth *</label>
                    <input
                        type="date"
                        value={stage2.date_of_birth}
                        onChange={(e) => setStage2({...stage2, date_of_birth: e.target.value})}
                        className="w-full px-3 py-2 border rounded"
                        required
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium mb-1">Gender *</label>
                    <select
                        value={stage2.gender}
                        onChange={(e) => setStage2({...stage2, gender: e.target.value})}
                        className="w-full px-3 py-2 border rounded"
                        required
                    >
                        <option value="">Select...</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="other">Other</option>
                        <option value="prefer_not_to_say">Prefer not to say</option>
                    </select>
                </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium mb-1">Marital Status *</label>
                    <select
                        value={stage2.marital_status}
                        onChange={(e) => setStage2({...stage2, marital_status: e.target.value})}
                        className="w-full px-3 py-2 border rounded"
                        required
                    >
                        <option value="">Select...</option>
                        <option value="single">Single</option>
                        <option value="married">Married</option>
                        <option value="divorced">Divorced</option>
                        <option value="widowed">Widowed</option>
                        <option value="separated">Separated</option>
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium mb-1">Number of Kids</label>
                    <input
                        type="number"
                        value={stage2.number_of_kids}
                        onChange={(e) => setStage2({...stage2, number_of_kids: e.target.value})}
                        className="w-full px-3 py-2 border rounded"
                        min="0"
                        placeholder="Enter number of children"
                    />
                </div>
            </div>
            <div>
                <label className="block text-sm font-medium mb-1">Current Occupation *</label>
                <input
                    type="text"
                    value={stage2.current_occupation}
                    onChange={(e) => setStage2({...stage2, current_occupation: e.target.value})}
                    className="w-full px-3 py-2 border rounded"
                    placeholder="e.g., Student, Software Developer, Teacher, Unemployed, etc."
                    required
                />
            </div>
        </div>
    );

    const renderStage3 = () => {
        return (
            <div className="space-y-4">
                <h3 className="text-xl font-bold">Stage 3: Identification & Documents</h3>
                
                {/* Identification Numbers */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <h4 className="font-semibold text-blue-900 mb-3">Identification Details</h4>
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium mb-1">
                                Government ID Number * <span className="text-gray-500 font-normal">(National ID or Voter ID)</span>
                            </label>
                            <input
                                type="text"
                                value={stage3.government_id_number}
                                onChange={(e) => setStage3({...stage3, government_id_number: e.target.value.toUpperCase()})}
                                placeholder="Enter your National ID or Voter ID number"
                                className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                required
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                Enter your National ID number or Voter ID number
                            </p>
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1">
                                Passport Number <span className="text-gray-400">(Optional)</span>
                            </label>
                            <input
                                type="text"
                                value={stage3.passport_number}
                                onChange={(e) => setStage3({...stage3, passport_number: e.target.value.toUpperCase()})}
                                placeholder="Enter your passport number (if available)"
                                className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                Optional: Enter your passport number if you have one
                            </p>
                        </div>
                    </div>
                </div>

                {/* Document Uploads */}
                <div className="space-y-4">
                    <h4 className="font-semibold text-gray-900 mb-3">Document Uploads</h4>
                    <div>
                        <label className="block text-sm font-medium mb-1">Government ID Document (optional)</label>
                        <p className="text-xs text-gray-500 mb-2">Upload a copy of your National ID or Voter ID</p>
                        <input
                            type="file"
                            accept="image/*,.pdf"
                            onChange={async (e) => {
                                const file = e.target.files[0];
                                if (file) {
                                    // Check file size (max 5MB)
                                    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
                                    if (file.size > maxSize) {
                                        toast.error('File size exceeds 5MB limit. Please upload a smaller file.');
                                        e.target.value = '';
                                        return;
                                    }
                                    try {
                                        setLoading(true);
                                        const response = await uploadAPI.uploadFile(file, true);
                                        setStage3({...stage3, government_id_path: response.file.filename});
                                        toast.success('Government ID uploaded successfully');
                                    } catch (err) {
                                        toast.error(err.message || 'Failed to upload file');
                                        e.target.value = '';
                                    } finally {
                                        setLoading(false);
                                    }
                                }
                            }}
                            className="w-full px-3 py-2 border rounded"
                        />
                        <p className="text-xs text-gray-500 mt-1">Maximum file size: 5MB</p>
                        {stage3.government_id_path && (
                            <p className="text-xs text-green-600 mt-1">✓ File uploaded: {stage3.government_id_path}</p>
                        )}
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Educational Certificates (optional)</label>
                        <input
                            type="file"
                            accept="image/*,.pdf"
                            onChange={async (e) => {
                                const file = e.target.files[0];
                                if (file) {
                                    // Check file size (max 5MB)
                                    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
                                    if (file.size > maxSize) {
                                        toast.error('File size exceeds 5MB limit. Please upload a smaller file.');
                                        e.target.value = '';
                                        return;
                                    }
                                    try {
                                        setLoading(true);
                                        const response = await uploadAPI.uploadFile(file, true);
                                        setStage3({...stage3, educational_certificates_path: response.file.filename});
                                        toast.success('Educational certificates uploaded successfully');
                                    } catch (err) {
                                        toast.error(err.message || 'Failed to upload file');
                                        e.target.value = '';
                                    } finally {
                                        setLoading(false);
                                    }
                                }
                            }}
                            className="w-full px-3 py-2 border rounded"
                        />
                        <p className="text-xs text-gray-500 mt-1">Maximum file size: 5MB</p>
                        {stage3.educational_certificates_path && (
                            <p className="text-xs text-green-600 mt-1">✓ File uploaded: {stage3.educational_certificates_path}</p>
                        )}
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Passport Photo *</label>
                        <input
                            type="file"
                            accept="image/*"
                            onChange={async (e) => {
                                const file = e.target.files[0];
                                if (file) {
                                    // Check file size (max 2MB for images)
                                    const maxSize = 2 * 1024 * 1024; // 2MB in bytes
                                    if (file.size > maxSize) {
                                        toast.error('Image size exceeds 2MB limit. Please upload a smaller image.');
                                        e.target.value = '';
                                        return;
                                    }
                                    try {
                                        setLoading(true);
                                        const response = await uploadAPI.uploadFile(file, true);
                                        setStage3({...stage3, passport_photo_path: response.file.filename});
                                        toast.success('Passport photo uploaded successfully');
                                    } catch (err) {
                                        toast.error(err.message || 'Failed to upload file');
                                        e.target.value = '';
                                    } finally {
                                        setLoading(false);
                                    }
                                }
                            }}
                            className="w-full px-3 py-2 border rounded"
                            required
                        />
                        <p className="text-xs text-gray-500 mt-1">Maximum file size: 2MB</p>
                        {stage3.passport_photo_path && (
                            <p className="text-xs text-green-600 mt-1">✓ File uploaded: {stage3.passport_photo_path}</p>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const renderStage4 = () => (
        <div className="space-y-4">
            <h3 className="text-xl font-bold">Stage 4: Motivational Questions</h3>
            <div>
                <label className="block text-sm font-medium mb-1">Why do you want this scholarship? *</label>
                <textarea
                    value={stage4.motivation_text}
                    onChange={(e) => setStage4({...stage4, motivation_text: e.target.value})}
                    className="w-full px-3 py-2 border rounded"
                    rows={4}
                    required
                />
            </div>
            <div>
                <label className="block text-sm font-medium mb-1">Career Goals (5 years) *</label>
                <textarea
                    value={stage4.career_goals}
                    onChange={(e) => setStage4({...stage4, career_goals: e.target.value})}
                    className="w-full px-3 py-2 border rounded"
                    rows={4}
                    required
                />
            </div>
            <div>
                <label className="block text-sm font-medium mb-1">Financial Background</label>
                <textarea
                    value={stage4.financial_background}
                    onChange={(e) => setStage4({...stage4, financial_background: e.target.value})}
                    className="w-full px-3 py-2 border rounded"
                    rows={3}
                />
            </div>
            
            {/* Code of Conduct */}
            <div className="border rounded-lg p-4 bg-gray-50">
                <div className="flex items-start mb-3">
                    <input
                        type="checkbox"
                        id="code_of_conduct"
                        checked={stage4.code_of_conduct_agreed}
                        onChange={(e) => setStage4({...stage4, code_of_conduct_agreed: e.target.checked})}
                        className="mt-1 mr-2"
                        required
                    />
                    <label htmlFor="code_of_conduct" className="text-sm flex-1">
                        I agree to the Code of Conduct *
                    </label>
                    <button
                        type="button"
                        onClick={() => setStage4({...stage4, show_code_of_conduct: true})}
                        className="text-blue-600 hover:text-blue-800 text-sm underline ml-2"
                    >
                        View Code of Conduct
                    </button>
                </div>
            </div>

            {/* Relocation Question */}
            <div className="border rounded-lg p-4 bg-gray-50">
                <label className="block text-sm font-medium mb-3">
                    If you are not currently residing in Ojoto, would you be willing to relocate to Ojoto temporarily, for six months? *
                </label>
                <div className="flex gap-4">
                    <label className="flex items-center">
                        <input
                            type="radio"
                            name="willing_to_relocate"
                            value="yes"
                            checked={stage4.willing_to_relocate === true}
                            onChange={() => setStage4({...stage4, willing_to_relocate: true})}
                            className="mr-2"
                            required
                        />
                        YES
                    </label>
                    <label className="flex items-center">
                        <input
                            type="radio"
                            name="willing_to_relocate"
                            value="no"
                            checked={stage4.willing_to_relocate === false}
                            onChange={() => setStage4({...stage4, willing_to_relocate: false})}
                            className="mr-2"
                            required
                        />
                        NO
                    </label>
                </div>
            </div>

            {/* Equipment Question */}
            <div className="border rounded-lg p-4 bg-gray-50">
                <label className="block text-sm font-medium mb-3">
                    If you are not currently residing in Ojoto and not willing to relocate to Ojoto temporarily, do you have a personal desk/laptop, STEADY power supply and internet access? *
                </label>
                <div className="flex gap-4">
                    <label className="flex items-center">
                        <input
                            type="radio"
                            name="has_equipment"
                            value="yes"
                            checked={stage4.has_equipment === true}
                            onChange={() => setStage4({...stage4, has_equipment: true})}
                            className="mr-2"
                            required
                        />
                        YES
                    </label>
                    <label className="flex items-center">
                        <input
                            type="radio"
                            name="has_equipment"
                            value="no"
                            checked={stage4.has_equipment === false}
                            onChange={() => setStage4({...stage4, has_equipment: false})}
                            className="mr-2"
                            required
                        />
                        NO
                    </label>
                </div>
            </div>

            {/* Code of Conduct Modal */}
            {stage4.show_code_of_conduct && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
                            <h3 className="text-xl font-bold">Code of Conduct for Scholarship Applicants</h3>
                            <button
                                onClick={() => setStage4({...stage4, show_code_of_conduct: false})}
                                className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
                            >
                                ×
                            </button>
                        </div>
                        <div className="p-6 space-y-4">
                            <p className="text-sm text-gray-600">
                                All applicants selected for the Ojoto Union Scholarship-Based IT Training Program are expected to uphold the highest standards of discipline, professionalism, and commitment throughout the duration of the program.
                            </p>
                            <p className="text-sm text-gray-600">
                                By accepting this scholarship, each participant agrees to the following code of conduct:
                            </p>

                            <div className="space-y-4">
                                <div>
                                    <h4 className="font-semibold text-gray-900 mb-2">1. Commitment to Learning</h4>
                                    <p className="text-sm text-gray-700">Participants must demonstrate consistent dedication to the program by:</p>
                                    <ul className="list-disc list-inside text-sm text-gray-700 ml-4 mt-1">
                                        <li>Attending all scheduled training sessions</li>
                                        <li>Completing assignments, quizzes, and projects on time</li>
                                        <li>Actively engaging in learning activities</li>
                                    </ul>
                                </div>

                                <div>
                                    <h4 className="font-semibold text-gray-900 mb-2">2. Regular Attendance and Participation</h4>
                                    <p className="text-sm text-gray-700">Students are expected to:</p>
                                    <ul className="list-disc list-inside text-sm text-gray-700 ml-4 mt-1">
                                        <li>Maintain regular attendance</li>
                                        <li>Notify mentors or coordinators in advance if unable to attend</li>
                                        <li>Participate actively during sessions through questions, discussions, and collaboration</li>
                                    </ul>
                                </div>

                                <div>
                                    <h4 className="font-semibold text-gray-900 mb-2">3. Professional Behavior</h4>
                                    <p className="text-sm text-gray-700">All participants must:</p>
                                    <ul className="list-disc list-inside text-sm text-gray-700 ml-4 mt-1">
                                        <li>Show respect to instructors, mentors, committee members, and fellow students</li>
                                        <li>Maintain a positive and cooperative attitude</li>
                                        <li>Avoid disruptive or inappropriate behavior during training</li>
                                    </ul>
                                </div>

                                <div>
                                    <h4 className="font-semibold text-gray-900 mb-2">4. Integrity and Honesty</h4>
                                    <p className="text-sm text-gray-700">Applicants and participants must uphold academic and professional integrity by:</p>
                                    <ul className="list-disc list-inside text-sm text-gray-700 ml-4 mt-1">
                                        <li>Submitting original work</li>
                                        <li>Avoiding plagiarism, cheating, or misrepresentation</li>
                                        <li>Providing truthful information during the application and training process</li>
                                    </ul>
                                </div>

                                <div>
                                    <h4 className="font-semibold text-gray-900 mb-2">5. Responsible Use of Resources</h4>
                                    <p className="text-sm text-gray-700">Students must use all learning resources responsibly, including:</p>
                                    <ul className="list-disc list-inside text-sm text-gray-700 ml-4 mt-1">
                                        <li>Training materials</li>
                                        <li>Online platforms</li>
                                        <li>Community center facilities (if applicable)</li>
                                    </ul>
                                    <p className="text-sm text-gray-700 mt-1">Any misuse or damage may result in disqualification.</p>
                                </div>

                                <div>
                                    <h4 className="font-semibold text-gray-900 mb-2">6. Respectful Communication</h4>
                                    <p className="text-sm text-gray-700">Participants are expected to communicate respectfully in all interactions, including:</p>
                                    <ul className="list-disc list-inside text-sm text-gray-700 ml-4 mt-1">
                                        <li>Online group discussions</li>
                                        <li>Emails and messages</li>
                                        <li>Mentorship sessions</li>
                                    </ul>
                                    <p className="text-sm text-gray-700 mt-1">Harassment, discrimination, or abusive language will not be tolerated.</p>
                                </div>

                                <div>
                                    <h4 className="font-semibold text-gray-900 mb-2">7. Commitment to Community Growth</h4>
                                    <p className="text-sm text-gray-700">This scholarship is a community-driven initiative. Students are encouraged to:</p>
                                    <ul className="list-disc list-inside text-sm text-gray-700 ml-4 mt-1">
                                        <li>Support and motivate fellow participants</li>
                                        <li>Share knowledge and collaborate</li>
                                        <li>Represent Ojoto Union with pride and responsibility</li>
                                    </ul>
                                </div>

                                <div>
                                    <h4 className="font-semibold text-gray-900 mb-2">8. Compliance with Program Rules</h4>
                                    <p className="text-sm text-gray-700">Participants must follow all guidelines provided by the Technology Project Committee, including:</p>
                                    <ul className="list-disc list-inside text-sm text-gray-700 ml-4 mt-1">
                                        <li>Internship requirements</li>
                                        <li>Evaluation criteria</li>
                                        <li>Program timelines and milestones</li>
                                    </ul>
                                    <p className="text-sm text-gray-700 mt-1">Failure to comply may lead to suspension or removal from the program.</p>
                                </div>

                                <div>
                                    <h4 className="font-semibold text-gray-900 mb-2">9. Scholarship Responsibility</h4>
                                    <p className="text-sm text-gray-700">As beneficiaries of a fully funded opportunity, students are expected to:</p>
                                    <ul className="list-disc list-inside text-sm text-gray-700 ml-4 mt-1">
                                        <li>Take the program seriously</li>
                                        <li>Use the opportunity to build skills and career readiness</li>
                                        <li>Become future contributors to Ojoto community development</li>
                                    </ul>
                                </div>

                                <div className="border-t pt-4 mt-4">
                                    <h4 className="font-semibold text-gray-900 mb-2">Acknowledgement</h4>
                                    <p className="text-sm text-gray-700">
                                        By submitting an application and accepting selection into the program, applicants acknowledge that they have read, understood, and agreed to abide by this Code of Conduct.
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="sticky bottom-0 bg-gray-50 border-t px-6 py-4 flex justify-end">
                            <button
                                onClick={() => setStage4({...stage4, show_code_of_conduct: false})}
                                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );

    // Render OTP step
    if (!otpVerified) {
        return (
            <div className="min-h-screen bg-gray-50 py-8">
                <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
                    <h1 className="text-3xl font-bold mb-6 text-gray-900">Scholarship Application - Email Verification</h1>
                    
                    {!otpSent ? (
                        <div className="bg-white rounded-lg shadow p-8">
                            <h2 className="text-xl font-semibold mb-4">Step 1: Verify Your Email</h2>
                            {(import.meta.env.DEV || window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') && (
                                <div className="bg-yellow-100 border border-yellow-400 text-yellow-800 px-4 py-3 rounded mb-4">
                                    <p className="text-sm font-semibold">🔧 DEVELOPMENT MODE</p>
                                    <p className="text-xs mt-1">OTP will be auto-verified. Just enter your email and click "Send Verification Code".</p>
                                </div>
                            )}
                            <p className="text-gray-600 mb-6">
                                Please enter your email address to receive a verification code.
                            </p>
                            
                            <form onSubmit={handleSendOTP} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">Email Address *</label>
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="w-full px-3 py-2 border rounded"
                                        required
                                        placeholder="your.email@example.com"
                                    />
                                </div>
                                
                                {error && (
                                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                                        {error}
                                    </div>
                                )}
                                
                                <button
                                    type="submit"
                                    disabled={sendingOtp || !email}
                                    className="w-full px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                                >
                                    {sendingOtp ? 'Sending...' : 'Send Verification Code'}
                                </button>
                            </form>
                        </div>
                    ) : (
                        <OTPVerification
                            email={email}
                            onVerified={handleOTPVerified}
                            onResend={handleOTPResend}
                        />
                    )}
                </div>
            </div>
        );
    }

    // Render application form
    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                <h1 className="text-3xl font-bold mb-6 text-gray-900">Scholarship Application</h1>

                {sessionsLoading ? (
                    <div className="bg-white rounded-lg shadow p-8 text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                        <p className="text-gray-600">Loading available sessions...</p>
                    </div>
                ) : !sessionId && sessions.length === 0 ? (
                    <div className="bg-white rounded-lg shadow p-8">
                        <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-4">
                            <p className="font-semibold">No active sessions available</p>
                            <p className="text-sm mt-1">There are currently no scholarship sessions open for applications. Please check back later or contact support for more information.</p>
                        </div>
                        <button
                            onClick={() => navigate('/')}
                            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                        >
                            Return to Home
                        </button>
                    </div>
                ) : sessionId ? (
                    <>
                        <div className="mb-6">
                            <div className="flex justify-between mb-2">
                                {[1, 2, 3, 4].map((stage) => {
                                    const isCompleted = stage < currentStage;
                                    const isCurrent = stage === currentStage;
                                    const canAccess = stage === 1 || (stage > 1 && isStageValid(stage - 1));
                                    
                                    return (
                                        <div
                                            key={stage}
                                            onClick={() => {
                                                if (canAccess && stage <= currentStage) {
                                                    setCurrentStage(stage);
                                                    setError('');
                                                }
                                            }}
                                            className={`flex-1 text-center py-2 cursor-pointer transition-colors ${
                                                isCurrent
                                                    ? 'bg-blue-600 text-white'
                                                    : isCompleted
                                                    ? 'bg-green-500 text-white'
                                                    : canAccess
                                                    ? 'bg-gray-300 text-gray-700 hover:bg-gray-400'
                                                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                            }`}
                                            title={!canAccess ? 'Complete previous stages first' : ''}
                                        >
                                            Stage {stage}
                                            {isCompleted && <span className="ml-1">✓</span>}
                                        </div>
                                    );
                                })}
                            </div>
                            <p className="text-xs text-gray-600 text-center mt-2">
                                Complete each stage in order. You cannot skip stages.
                            </p>
                        </div>

                        {error && (
                            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                                {error}
                            </div>
                        )}

                        <div className="bg-white p-6 rounded-lg shadow">
                            {currentStage === 1 && renderStage1()}
                            {currentStage === 2 && renderStage2()}
                            {currentStage === 3 && renderStage3()}
                            {currentStage === 4 && renderStage4()}

                            <div className="mt-6 flex justify-between items-center">
                                <button
                                    onClick={() => {
                                        setCurrentStage(Math.max(1, currentStage - 1));
                                        setError('');
                                    }}
                                    disabled={currentStage === 1 || loading}
                                    className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    ← Previous
                                </button>
                                
                                <div className="text-sm text-gray-600">
                                    Stage {currentStage} of 4
                                </div>
                                
                                <button
                                    onClick={handleStageSubmit}
                                    disabled={loading}
                                    className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                                >
                                    {loading
                                        ? 'Saving...'
                                        : currentStage === 4
                                        ? '✓ Submit Application'
                                        : 'Next →'}
                                </button>
                            </div>
                        </div>
                    </>
                ) : null}
            </div>
        </div>
    );
};

export default PublicApplicationForm;
